import { useState } from 'react';
import { Box, Text } from '@nimbus-ds/components';
import { AI_FEEDBACK_SCORE } from '@tiendanube/common/src/common/AiFeedbackConstants';
import { AiFeature } from '@tiendanube/common/src/enums';
import { useAgentChatPanelContext } from 'App/components/AgentChatPanelContext';
import { useAppDispatch } from 'App/store';
import { AgentAvatarIcon } from 'commons/components/AgentChat/img';
import { AiFeedbackModal } from 'commons/components/AiFeedbackModal';
import {
  FeedbackButtons,
  type FeedbackType,
} from 'commons/components/FeedbackButtons';
import {
  useTranslationCommon,
  useTranslationLanguage,
  useToast,
} from 'commons/hooks';
import useProvideAiFeedback from 'commons/hooks/useProvideAiFeedback';
import log, { logError } from 'commons/logger';
import { clearAllStates } from 'commons/store/AiGeneration/aiGenerationSlice';
import { ChatMessageAction } from './components';
import type { ChatMessageProps } from './types';
import { useAgentChatContext } from '../../contexts/AgentChatContext';
import { trackAgentChatActionAccepted } from '../../tracking';
import {
  isNavigationLinkSegment,
  parseMessageContent,
} from '../../utils/mappings';
import { ChatHelpLinks } from '../ChatHelpLinks';
import { ChatMarkdownParser } from '../ChatMarkdownParser';

/**
 * AIMessage component renders AI messages with left alignment, avatar, and action chain functionality
 * Confirmation and feedback states are managed at the context level for proper persistence across renders
 */
function ChatAIMessage({ message }: Readonly<ChatMessageProps>): JSX.Element {
  const { actionBus } = useAgentChatPanelContext();
  const dispatch = useAppDispatch();

  const { addToast } = useToast();
  const t = useTranslationCommon('ai.agent');
  const locale = useTranslationLanguage();
  const { updateMessageConfirmation, updateMessageFeedback, addErrorMessage } =
    useAgentChatContext();
  const { sendAiGenerationFeedback } = useProvideAiFeedback();
  const [showAiGenerationFeedbackModal, setShowAiGenerationFeedbackModal] =
    useState(false);

  const confirmed = message.confirmed ?? false;
  const feedbackGiven = message.feedback ?? null;
  const formattedTime = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(message.timestamp);
  const actionChainErrorMessage = t('actionChainError');
  const hasActionChain =
    message.actionChain !== undefined && message.actionChain.length > 0;

  const handleNegativeFeedback = () => {
    setShowAiGenerationFeedbackModal(true);
  };

  const handleSendPositiveFeedback = async () => {
    try {
      addToast({
        label: t('feedbackSuccess'),
        appearance: 'success',
      });
      updateMessageFeedback(message.id, 1);
      await sendAiGenerationFeedback({
        feedback: 'Good feedback for AI action chain',
        feature: AiFeature.AI_AGENT_CHAT,
        run_id: message.id,
        score: AI_FEEDBACK_SCORE.POSITIVE,
      });
    } catch (error) {
      logError('Error sending positive feedback', error as Error, {
        messageId: message.id,
      });
      return;
    }
  };

  const handleConfirmAction = () => {
    if (!actionBus || !message.actionChain?.length) {
      logError(
        'Action bus or action chain is empty',
        new Error('Action bus or action chain is empty'),
        {
          actionChain: message.actionChain,
        },
      );
      addErrorMessage(actionChainErrorMessage);
      return;
    }

    try {
      dispatch(clearAllStates());
      actionBus.dispatchBatch(message.actionChain);
    } catch (error) {
      logError('Failed to run batch operations', error as Error, {
        messageId: message.id,
      });
      addErrorMessage(actionChainErrorMessage);
    } finally {
      trackAgentChatActionAccepted({
        runId: message.id || null,
        sessionId: message.sessionId ?? null,
        actions: message.actionChain,
      });
      updateMessageConfirmation(message.id, true);
    }
  };
  const messageSegments = parseMessageContent(message.content);
  const hasPreviewSegments = messageSegments.some((s) => s.type === 'preview');

  const mapFeedbackValue = (value: number | null): FeedbackType => {
    if (value === 1) return 'positive';
    if (value === -1) return 'negative';
    return null;
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        gap="2"
        alignItems="flex-start"
      >
        <Box display="flex" gap="2" alignItems="center">
          <AgentAvatarIcon width={30} height={30} />

          <Box display="flex" flexDirection="column">
            <Text
              fontSize="base"
              color="neutral-textHigh"
              lineHeight="base"
              fontWeight="medium"
            >
              {t('title')}
            </Text>
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap="2"
          pl="8"
          maxWidth="100%"
        >
          <Box display="flex" flexDirection="column" gap="4">
            {messageSegments.map((segment, index) => {
              if (segment.type === 'text') {
                return (
                  <Box key={segment.id} pr="4">
                    <Text
                      fontSize="base"
                      color="neutral-textHigh"
                      lineHeight="base"
                      wordBreak="break-word"
                    >
                      {segment.content}
                    </Text>
                  </Box>
                );
              }

              const isLastPreview =
                index === messageSegments.length - 1 ||
                messageSegments
                  .slice(index + 1)
                  .every((s) => s.type === 'text');

              const isNavigationOnly = isNavigationLinkSegment(segment.content);

              if (isNavigationOnly) {
                return (
                  <Box key={segment.id}>
                    <ChatMarkdownParser
                      content={segment.content}
                      confirmed={confirmed}
                    />
                  </Box>
                );
              }

              return (
                <Box
                  key={segment.id}
                  padding="4"
                  borderRadius="2"
                  borderColor="neutral-surfaceHighlight"
                  borderStyle="solid"
                  borderWidth="1"
                  display="flex"
                  flexDirection="column"
                >
                  <ChatMarkdownParser
                    content={segment.content}
                    confirmed={confirmed}
                  />

                  {hasActionChain && isLastPreview && (
                    <Box mt="4">
                      <ChatMessageAction
                        confirmed={confirmed}
                        onClick={handleConfirmAction}
                        label={t('confirm')}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}

            {hasActionChain && !hasPreviewSegments && (
              <Box>
                <ChatMessageAction
                  confirmed={confirmed}
                  onClick={handleConfirmAction}
                  label={t('confirm')}
                />
              </Box>
            )}
          </Box>

          {message.helpLinks && message.helpLinks.length > 0 && (
            <Box mt="2">
              <ChatHelpLinks helpLinks={message.helpLinks} />
            </Box>
          )}

          <Box display="flex" justifyContent="space-between">
            <Box display="flex" gap="2" alignItems="center">
              <FeedbackButtons
                tooltipPosition="bottom"
                feedbackGiven={mapFeedbackValue(feedbackGiven)}
                onPositiveFeedback={handleSendPositiveFeedback}
                onNegativeFeedback={handleNegativeFeedback}
                thumbsUpAriaLabel={t('feedbackPositive')}
                thumbsDownAriaLabel={t('feedbackNegative')}
              />
            </Box>

            <Text fontSize="caption" color="neutral-textLow">
              {formattedTime}
            </Text>
          </Box>
        </Box>
      </Box>
      <AiFeedbackModal
        show={showAiGenerationFeedbackModal}
        title={t('feedbackModal.title')}
        text={t('feedbackModal.text')}
        feedbackParams={{
          run_id: message.id,
          score: AI_FEEDBACK_SCORE.NEGATIVE,
        }}
        asAside={false}
        feature={AiFeature.AI_AGENT_CHAT}
        onDismiss={() => {
          setShowAiGenerationFeedbackModal(false);
        }}
        onConfirm={() => {
          log({
            message: 'User provided negative feedback on AI message',
            level: 'info',
            location: window.location.href,
            data: {
              messageId: message.id,
            },
          });
          updateMessageFeedback(message.id, -1);
        }}
      />
    </>
  );
}

export default ChatAIMessage;
